import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { track } from '../analytics';
import { ProgressTrail } from '../components/ProgressTrail';
import { HelpIllustration } from '../components/HelpIllustration';
import { diagnosisConfig } from '../diagnosis/config';
import {
  chooseOption,
  createSession,
  escalateOutcome,
  getCurrentOutcome,
  getIssue,
  stepBack,
  validateConfig,
} from '../diagnosis/engine';
import { clearSession, loadSession, saveSession } from '../diagnosis/session';
import { buildDiagnosisCode, buildSummary } from '../diagnosis/summary';
import type { DiagnosisSession } from '../diagnosis/types';

validateConfig(diagnosisConfig);

export function DiagnosisPage() {
  const { issueId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const issue = getIssue(diagnosisConfig, issueId);
  const shouldRestart = new URLSearchParams(location.search).get('restart') === '1';
  const [session, setSession] = useState<DiagnosisSession | undefined>(() => {
    if (!issue) return undefined;
    if (shouldRestart) return createSession(issue);
    return loadSession(issue.id) ?? createSession(issue);
  });
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const headingRef = useRef<HTMLHeadingElement>(null);
  const trackedIssueRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!issue) return;
    const restartRequested = new URLSearchParams(location.search).get('restart') === '1';
    if (restartRequested) clearSession(issue.id);
    const restored = restartRequested
      ? createSession(issue)
      : loadSession(issue.id) ?? createSession(issue);
    setSession(restored);
    if (trackedIssueRef.current !== issue.id) {
      track({ name: 'diagnosis_started', payload: { issueId: issue.id } });
      trackedIssueRef.current = issue.id;
    }
    if (restartRequested) {
      navigate(location.pathname, { replace: true });
    }
  }, [issue, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (session) saveSession(session);
    headingRef.current?.focus();
  }, [session]);

  const outcome = session ? getCurrentOutcome(diagnosisConfig, session) : undefined;
  const currentNode = session?.currentNodeId
    ? diagnosisConfig.nodes[session.currentNodeId]
    : undefined;
  const summary = useMemo(() => {
    if (!session || !outcome) return '';
    return buildSummary(diagnosisConfig, session);
  }, [session, outcome]);

  if (!issue || !session) {
    return (
      <div className="page narrow-page">
        <section className="result-card">
          <span className="eyebrow">没有找到这个问题</span>
          <h1>请从首页重新选择</h1>
          <Link className="primary-button" to="/">返回问题列表</Link>
        </section>
      </div>
    );
  }

  function restart() {
    clearSession(issue!.id);
    setSession(createSession(issue!));
    setCopyState('idle');
  }

  async function copySummary() {
    if (!outcome) return;
    try {
      await navigator.clipboard.writeText(summary);
      setCopyState('copied');
      track({
        name: 'summary_copied',
        payload: { issueId: issue!.id, outcomeKind: outcome.kind },
      });
    } catch {
      setCopyState('failed');
    }
  }

  if (outcome) {
    const resultCode = buildDiagnosisCode(issue, outcome);
    return (
      <div className="page narrow-page">
        <ProgressTrail current={session.answers.length + 1} total={session.answers.length + 1} />
        <section className={`result-card result-${outcome.kind}`} aria-labelledby="result-title">
          <div className="result-code">{resultCode}</div>
          <span className="eyebrow">排查完成</span>
          <h1 id="result-title" ref={headingRef} tabIndex={-1}>{outcome.title}</h1>
          <p className="result-body">{outcome.body}</p>
          <div className="recommendation">
            <strong>接下来这样做</strong>
            <p>{outcome.recommendation}</p>
          </div>

          {outcome.illustration && (
            <HelpIllustration illustration={outcome.illustration} />
          )}

          {outcome.checklist && (
            <ol className="result-checklist">
              {outcome.checklist.map((item) => <li key={item}>{item}</li>)}
            </ol>
          )}

          {outcome.primaryAction?.href && (
            <a
              className="primary-button"
              href={outcome.primaryAction.href}
              rel="noreferrer"
              target="_blank"
            >
              {outcome.primaryAction.label}
            </a>
          )}

          {outcome.fallbackOutcomeId && (
            <button
              className="secondary-button outcome-fallback-button"
              type="button"
              onClick={() => {
                const nextSession = escalateOutcome(diagnosisConfig, session);
                const nextOutcome = getCurrentOutcome(diagnosisConfig, nextSession);
                setSession(nextSession);
                setCopyState('idle');
                if (nextOutcome) {
                  track({
                    name: 'diagnosis_completed',
                    payload: { issueId: issue.id, outcomeKind: nextOutcome.kind },
                  });
                }
              }}
            >
              {outcome.fallbackLabel ?? '已经尝试，仍没解决'}
            </button>
          )}

          {outcome.kind === 'seller_review' && (
            <div className="summary-panel">
              <div className="summary-heading">
                <div>
                  <span className="eyebrow">发给卖家</span>
                  <h2>复制诊断摘要</h2>
                </div>
                <span className="privacy-chip">不含账号信息</span>
              </div>
              <textarea aria-label="诊断摘要" readOnly rows={8} value={summary} />
              <button className="primary-button" type="button" onClick={copySummary}>
                {copyState === 'copied' ? '已复制，可以回闲鱼发送' : '复制诊断摘要'}
              </button>
              {copyState === 'failed' && (
                <p className="inline-error" role="alert">自动复制失败，请长按上方文字手动复制。</p>
              )}
            </div>
          )}

          <div className="result-actions">
            <button className="secondary-button" type="button" onClick={restart}>重新排查</button>
            <button className="text-button" type="button" onClick={() => navigate('/')}>选择其他问题</button>
          </div>
        </section>
      </div>
    );
  }

  if (!currentNode) {
    return null;
  }

  return (
    <div className="page narrow-page">
      <ProgressTrail current={session.answers.length + 1} total={4} />
      <section className="question-card" aria-labelledby="question-title">
        <span className="eyebrow">{currentNode.eyebrow}</span>
        <h1 id="question-title" ref={headingRef} tabIndex={-1}>{currentNode.title}</h1>
        {currentNode.description && <p className="question-description">{currentNode.description}</p>}

        {currentNode.warning && (
          <div className="warning-box" role="note">
            <strong>先别急着操作</strong>
            <p>{currentNode.warning}</p>
          </div>
        )}

        {currentNode.help && (
          <details
            className="help-details"
            key={currentNode.id}
            open={currentNode.help.defaultOpen}
          >
            <summary>{currentNode.help.title}</summary>
            <p>{currentNode.help.body}</p>
            {currentNode.help.illustration && (
              <HelpIllustration illustration={currentNode.help.illustration} />
            )}
            {currentNode.help.example && <div className="screen-example">{currentNode.help.example}</div>}
            {currentNode.help.href && (
              <a href={currentNode.help.href} target="_blank" rel="noreferrer">
                {currentNode.help.hrefLabel ?? '打开官方页面'}
              </a>
            )}
          </details>
        )}

        <div className="option-list" role="list">
          {currentNode.options.map((option) => (
            <button
              className="option-button"
              key={option.value}
              type="button"
              onClick={() => {
                const nextSession = chooseOption(diagnosisConfig, session, option.value);
                setSession(nextSession);
                if (nextSession.outcomeId) {
                  const nextOutcome = diagnosisConfig.outcomes[nextSession.outcomeId];
                  track({
                    name: 'diagnosis_completed',
                    payload: { issueId: issue.id, outcomeKind: nextOutcome.kind },
                  });
                }
              }}
            >
              <span>{option.label}</span>
              {option.helper && <small>{option.helper}</small>}
              <b aria-hidden="true">→</b>
            </button>
          ))}
        </div>

        <div className="question-footer">
          <button
            className="text-button"
            type="button"
            disabled={session.answers.length === 0}
            onClick={() => setSession(stepBack(diagnosisConfig, session))}
          >
            返回上一步
          </button>
          <span>{issue.title}</span>
        </div>
      </section>
    </div>
  );
}
